import React, { useState, useEffect } from "react";
import {
  LayoutGrid,
  Trophy,
  Sidebar as SidebarIcon,
  Monitor,
  Smartphone,
  Hand,
  MousePointerClick,
  Info,
  Save,
  RotateCcw,
  SlidersHorizontal,
  Check,
  Clock,
  Printer,
  ShoppingBag,
  PanelTop,
  PanelLeft,
  Calculator,
} from "lucide-react";
import {
  getBestSellerConfig,
  updateBestSellerConfig,
} from "../api/salesService";
import { configService } from "../api/configService";
import { salesTypeService } from "../api/masterService";
import SearchableSelect from "./SearchableSelect";
import { setReceiptConfig } from "../utils/receiptConfig";
import {
  useSidebarConfig,
  DEFAULT_SIDEBAR_CONFIG,
} from "../contexts/SidebarConfigContext";
import { useNotification } from "../contexts/NotificationContext";

// Reuseable Uniform Toggle Switch
function Toggle({ checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
        checked ? "bg-teal-600" : "bg-slate-300"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
      <span
        aria-hidden="true"
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

// Reuseable Uniform Setting Row
function SettingRow({ icon: Icon, title, description, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-4 border-b border-slate-100 last:border-b-0">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        {Icon && (
          <div className="w-9 h-9 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center shrink-0 mt-0.5">
            <Icon className="w-4 h-4" />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-800">{title}</p>
          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{description}</p>
        </div>
      </div>
      <div className="pt-0.5 shrink-0">{children}</div>
    </div>
  );
}

// Uniform Section Card Header
function SectionHeader({ icon: Icon, title, description }) {
  return (
    <div className="flex items-center gap-3 pb-4 border-b border-slate-100 mb-5">
      <div className="w-10 h-10 rounded-xl bg-teal-50 ring-1 ring-teal-200/70 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-teal-600" />
      </div>
      <div>
        <h3 className="text-base font-bold text-slate-900 tracking-tight">
          {title}
        </h3>
        <p className="text-xs text-slate-500">{description}</p>
      </div>
    </div>
  );
}

export default function OtherSettingsTab() {
  const { showSuccess, showError } = useNotification();
  const { config: sidebarConfig, updateConfig: updateSidebarConfig, resetConfig: resetSidebarConfig } = useSidebarConfig();

  // === 1. Catalog Config State ===
  const [catalogViewMode, setCatalogViewMode] = useState(() => {
    return localStorage.getItem("catalog_view_mode") || "list";
  });
  const [showListImage, setShowListImage] = useState(() => {
    const saved = localStorage.getItem("catalog_show_list_image");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [categoryPosition, setCategoryPosition] = useState(() => {
    return localStorage.getItem("catalog_category_position") || "top";
  });
  const [showProductStock, setShowProductStock] = useState(() => {
    const saved = localStorage.getItem("catalog_show_product_stock");
    return saved !== null ? JSON.parse(saved) : true;
  });

  // === 2. Best Seller Config State ===
  const [bestSellerLoading, setBestSellerLoading] = useState(true);
  const [bestSellerSaving, setBestSellerSaving] = useState(false);
  const [bestSellerConfig, setBestSellerConfig] = useState({
    enabled: true,
    min_orders: 2,
    max_products: 50,
    mode: "manual", // ALWAYS FORCE MANUAL
    manual_product_ids: [],
  });

  // === 3. Platform / Display Mode State ===
  const [displayMode, setDisplayMode] = useState("retail");

  // === 4. Auto Close Shift State ===
  const [autoCloseLoading, setAutoCloseLoading] = useState(true);
  const [autoCloseSaving, setAutoCloseSaving] = useState(false);
  const [autoCloseEnabled, setAutoCloseEnabled] = useState(false);
  const [autoCloseTime, setAutoCloseTime] = useState("23:59");

  // === 5. Auto Print Draft / Pending State ===
  const [autoPrintPendingEnabled, setAutoPrintPendingEnabled] = useState(false);
  const [autoPrintPendingSaving, setAutoPrintPendingSaving] = useState(false);

  // === 6. Default Sales Type State ===
  const [salesTypes, setSalesTypes] = useState([]);
  const [defaultSalesTypeId, setDefaultSalesTypeId] = useState("");
  const [defaultSalesTypeLoading, setDefaultSalesTypeLoading] = useState(true);
  const [defaultSalesTypeSaving, setDefaultSalesTypeSaving] = useState(false);

  // === 7. HPP / PO Valuation Method State ===
  const [hppMethod, setHppMethod] = useState(() => {
    return localStorage.getItem("hpp_valuation_method") || "STANDARD";
  });
  const [hppMethodSaving, setHppMethodSaving] = useState(false);

  useEffect(() => {
    setDisplayMode(localStorage.getItem("PREVIEW_APPS") === "true" ? "apps" : "retail");
    loadBestSeller();
    loadStoreSettings();
    loadSalesTypes();
  }, []);

  const loadSalesTypes = async () => {
    try {
      const res = await salesTypeService.list({ limit: 100 });
      const data = res?.data || [];
      setSalesTypes(data.filter((item) => item.status === "ACTIVE"));
    } catch (err) {
      console.error("Gagal memuat tipe penjualan:", err);
      showError("Gagal memuat daftar tipe penjualan");
    } finally {
      setDefaultSalesTypeLoading(false);
    }
  };

  const loadStoreSettings = async () => {
    try {
      setAutoCloseLoading(true);
      const res = await configService.getStore();
      const store = res?.data || res || {};
      const settings = store?.settings || {};
      setAutoCloseEnabled(Boolean(settings.auto_close_shift_enabled));
      const isRetailMode = localStorage.getItem("PREVIEW_APPS") !== "true";
      const storedAutoPrintPending = Boolean(settings.auto_print_pending_enabled);
      setAutoPrintPendingEnabled(isRetailMode ? false : storedAutoPrintPending);
      const storedCategoryPos = settings.category_position || localStorage.getItem("catalog_category_position") || "top";
      setCategoryPosition(storedCategoryPos);
      localStorage.setItem("catalog_category_position", storedCategoryPos);

      const storedShowProductStock = settings.show_product_stock !== undefined ? Boolean(settings.show_product_stock) : (localStorage.getItem("catalog_show_product_stock") !== null ? JSON.parse(localStorage.getItem("catalog_show_product_stock")) : true);
      setShowProductStock(storedShowProductStock);
      localStorage.setItem("catalog_show_product_stock", JSON.stringify(storedShowProductStock));

      setDefaultSalesTypeId(settings.default_sales_type_id || "");
      if (settings.default_sales_type_id) {
        localStorage.setItem("default_sales_type_id", settings.default_sales_type_id);
      }

      const storedHppMethod = settings.hpp_valuation_method || "STANDARD";
      setHppMethod(storedHppMethod);
      localStorage.setItem("hpp_valuation_method", storedHppMethod);

      // Bersihkan konfigurasi lama agar retail tidak pernah mencetak nota pending.
      if (isRetailMode && storedAutoPrintPending) {
        await configService.updateStore({
          name: store.name || "",
          address: store.address || "",
          phone: store.phone || "",
          settings: {
            ...settings,
            auto_print_pending_enabled: false,
          },
        });
      }
    } catch (err) {
      console.error("Gagal memuat konfigurasi toko:", err);
    } finally {
      setAutoCloseLoading(false);
    }
  };

  const handleSaveDefaultSalesType = async (salesTypeId) => {
    const previousId = defaultSalesTypeId;
    try {
      setDefaultSalesTypeId(salesTypeId);
      if (salesTypeId) {
        localStorage.setItem("default_sales_type_id", salesTypeId);
      } else {
        localStorage.removeItem("default_sales_type_id");
      }
      setDefaultSalesTypeSaving(true);
      const storeRes = await configService.getStore();
      const storeData = storeRes?.data || storeRes || {};
      const currentSettings = storeData.settings || {};

      await configService.updateStore({
        name: storeData.name || "",
        address: storeData.address || "",
        phone: storeData.phone || "",
        footer_note: currentSettings.footer_note || "",
        settings: {
          ...currentSettings,
          default_sales_type_id: salesTypeId,
        },
      });

      const selected = salesTypes.find((item) => String(item.id) === String(salesTypeId));
      showSuccess(`Tipe penjualan default diubah ke ${selected?.name || "pilihan tenant"}`);
    } catch (err) {
      setDefaultSalesTypeId(previousId);
      if (previousId) {
        localStorage.setItem("default_sales_type_id", previousId);
      }
      console.error("Gagal menyimpan tipe penjualan default:", err);
      showError("Gagal menyimpan tipe penjualan default");
    } finally {
      setDefaultSalesTypeSaving(false);
    }
  };

  const handleSaveHppMethod = async (methodVal) => {
    try {
      setHppMethodSaving(true);
      const storeRes = await configService.getStore().catch(() => null);
      const storeData = storeRes?.data || storeRes || {};
      const currentSettings = storeData.settings || {};

      await configService.updateStore({
        name: storeData.name || "",
        address: storeData.address || "",
        phone: storeData.phone || "",
        footer_note: currentSettings.footer_note || "",
        settings: {
          ...currentSettings,
          hpp_valuation_method: methodVal,
        },
      });

      setHppMethod(methodVal);
      localStorage.setItem("hpp_valuation_method", methodVal);
      showSuccess("Metode pencatatan HPP & Inventori berhasil diperbarui");
    } catch (err) {
      console.error("Gagal menyimpan metode HPP:", err);
      showError("Gagal menyimpan metode pencatatan HPP");
    } finally {
      setHppMethodSaving(false);
    }
  };

  const handleSaveAutoCloseShift = async (newEnabled, newTime) => {
    try {
      setAutoCloseSaving(true);
      const storeRes = await configService.getStore();
      const storeData = storeRes?.data || storeRes || {};
      const currentSettings = storeData.settings || {};

      const updatedSettings = {
        ...currentSettings,
        auto_close_shift_enabled: newEnabled,
        auto_close_shift_time: newTime,
      };

      await configService.updateStore({
        name: storeData.name || "",
        address: storeData.address || "",
        phone: storeData.phone || "",
        settings: updatedSettings,
      });

      setAutoCloseEnabled(newEnabled);
      setAutoCloseTime(newTime);
      showSuccess(`Konfigurasi Auto Close Shift berhasil disimpan (${newEnabled ? "Aktif jam " + newTime + " WIB" : "Non-Aktif"})`);
    } catch (err) {
      console.error("Gagal menyimpan konfigurasi auto close shift:", err);
      showError("Gagal menyimpan konfigurasi Auto Close Shift");
    } finally {
      setAutoCloseSaving(false);
    }
  };

  const handleSaveAutoPrintPending = async (newEnabled) => {
    try {
      setAutoPrintPendingSaving(true);
      const storeRes = await configService.getStore();
      const storeData = storeRes?.data || storeRes || {};
      const currentSettings = storeData.settings || {};

      const updatedSettings = {
        ...currentSettings,
        auto_print_pending_enabled: newEnabled,
      };

      await configService.updateStore({
        name: storeData.name || "",
        address: storeData.address || "",
        phone: storeData.phone || "",
        settings: updatedSettings,
      });

      setAutoPrintPendingEnabled(newEnabled);
      showSuccess(`Auto Print Nota Pesanan / Draft Pending (${newEnabled ? "Aktif" : "Non-Aktif"})`);
    } catch (err) {
      console.error("Gagal menyimpan konfigurasi auto print pending:", err);
      showError("Gagal menyimpan konfigurasi Auto Print Nota Pesanan");
    } finally {
      setAutoPrintPendingSaving(false);
    }
  };

  const loadBestSeller = async () => {
    try {
      setBestSellerLoading(true);
      const res = await getBestSellerConfig();
      const data = res?.data || {
        enabled: true,
        min_orders: 2,
        max_products: 50,
        mode: "manual",
        manual_product_ids: [],
      };
      // Force mode to manual only
      data.mode = "manual";
      if (!data.manual_product_ids) data.manual_product_ids = [];
      setBestSellerConfig(data);
    } catch (err) {
      console.error("Error loading best seller config:", err);
    } finally {
      setBestSellerLoading(false);
    }
  };

  // Handlers - Catalog
  const handleCatalogViewModeChange = (mode) => {
    setCatalogViewMode(mode);
    localStorage.setItem("catalog_view_mode", mode);
    showSuccess(`Tampilan katalog diubah ke Mode ${mode === "grid" ? "Gambar (Grid)" : "Daftar (List)"}`);
  };

  const handleToggleListImage = (val) => {
    setShowListImage(val);
    localStorage.setItem("catalog_show_list_image", JSON.stringify(val));
    showSuccess(`Gambar pada Mode List ${val ? "diaktifkan" : "dinonaktifkan"}`);
  };

  const handleToggleProductStock = async (val) => {
    setShowProductStock(val);
    localStorage.setItem("catalog_show_product_stock", JSON.stringify(val));
    showSuccess(`Tampilan stok pada card produk ${val ? "diaktifkan" : "dinonaktifkan"}`);

    try {
      const storeRes = await configService.getStore().catch(() => null);
      if (storeRes?.data) {
        const storeData = storeRes.data;
        const currentSettings = storeData.settings || {};
        await configService.updateStore({
          name: storeData.name,
          address: storeData.address,
          phone: storeData.phone,
          settings: {
            ...currentSettings,
            show_product_stock: val,
          },
        });
      }
    } catch (err) {
      console.error("Failed to save show_product_stock to DB:", err);
    }
  };

  const handleCategoryPositionChange = async (pos) => {
    setCategoryPosition(pos);
    localStorage.setItem("catalog_category_position", pos);
    showSuccess(`Posisi kategori diubah ke ${pos === "side" ? "Mode Samping Kiri (Vertikal)" : "Mode Atas (Horizontal)"}`);

    try {
      const storeRes = await configService.getStore().catch(() => null);
      if (storeRes?.data) {
        const storeData = storeRes.data;
        const currentSettings = storeData.settings || {};
        await configService.updateStore({
          name: storeData.name,
          address: storeData.address,
          phone: storeData.phone,
          settings: {
            ...currentSettings,
            category_position: pos,
          },
        });
      }
    } catch (err) {
      console.error("Failed to save category position to DB:", err);
    }
  };

  // Handlers - Best Seller Toggle
  const handleToggleBestSeller = async (enabled) => {
    const nextConfig = { ...bestSellerConfig, enabled, mode: "manual" };
    setBestSellerConfig(nextConfig);
    try {
      setBestSellerSaving(true);
      await updateBestSellerConfig(nextConfig);
      showSuccess(`Fitur Best Seller ${enabled ? "diaktifkan" : "dinonaktifkan"}`);
    } catch (err) {
      console.error("Failed to update best seller config:", err);
      showError("Gagal mengubah pengaturan Best Seller");
      // revert
      setBestSellerConfig((prev) => ({ ...prev, enabled: !enabled }));
    } finally {
      setBestSellerSaving(false);
    }
  };

  // Handlers - Display Mode (Retail vs Apps)
  const handleDisplayModeChange = async (mode) => {
    try {
      setDisplayMode(mode);
      localStorage.setItem("PREVIEW_APPS", mode === "apps" ? "true" : "false");

      // Retail tidak memiliki nota pesanan, label, atau auto print pending.
      if (mode === "retail") {
        setReceiptConfig({
          orderLabelEnabled: false,
          autoPrintLabelEnabled: false,
          orderReceiptEnabled: false,
        });
        setAutoPrintPendingEnabled(false);
      }

      // Mode Kuliner/Apps: aktifkan kembali cetak nota pesanan (order)
      // agar Split Nota Pesanan & tombol Cetak Nota Pesanan berfungsi otomatis.
      if (mode === "apps") {
        setReceiptConfig({ orderReceiptEnabled: true });
      }

      const storeRes = await configService.getStore().catch(() => null);
      if (storeRes?.data) {
        const storeData = storeRes.data;
        const currentSettings = storeData.settings || {};
        await configService.updateStore({
          name: storeData.name,
          address: storeData.address,
          phone: storeData.phone,
          settings: {
            ...currentSettings,
            display_mode: mode,
            ...(mode === "retail" ? { auto_print_pending_enabled: false } : {}),
          },
        });
      }
    } catch (err) {
      console.error("Failed to save display mode to DB:", err);
    } finally {
      window.location.replace(window.location.origin + "/");
    }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      {/* SECTION 1: PENGATURAN KATALOG */}
      <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm p-6">
        <SectionHeader
          icon={LayoutGrid}
          title="Pengaturan Katalog"
          description="Atur mode tampilan standar untuk katalog produk di halaman penjualan (Kasir)."
        />

        <div className="space-y-5">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
              Mode Tampilan Default Katalog
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => handleCatalogViewModeChange("grid")}
                className={`p-4 rounded-xl border-2 transition-all text-left flex items-start gap-3.5 ${
                  catalogViewMode === "grid"
                    ? "border-teal-500 bg-teal-50/50 ring-1 ring-teal-500/20"
                    : "border-slate-200 hover:border-teal-200 bg-white"
                }`}>
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                    catalogViewMode === "grid"
                      ? "bg-teal-600 text-white"
                      : "bg-slate-100 text-slate-500"
                  }`}>
                  <LayoutGrid className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-slate-800">Mode Gambar (Grid)</span>
                    {catalogViewMode === "grid" && (
                      <span className="w-2 h-2 rounded-full bg-teal-600" />
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                    Menampilkan produk dalam bentuk kartu kotak dengan gambar jelas. Cocok untuk layar sentuh / tablet.
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleCatalogViewModeChange("list")}
                className={`p-4 rounded-xl border-2 transition-all text-left flex items-start gap-3.5 ${
                  catalogViewMode === "list"
                    ? "border-teal-500 bg-teal-50/50 ring-1 ring-teal-500/20"
                    : "border-slate-200 hover:border-teal-200 bg-white"
                }`}>
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                    catalogViewMode === "list"
                      ? "bg-teal-600 text-white"
                      : "bg-slate-100 text-slate-500"
                  }`}>
                  <SlidersHorizontal className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-slate-800">Mode Daftar (List)</span>
                    {catalogViewMode === "list" && (
                      <span className="w-2 h-2 rounded-full bg-teal-600" />
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                    Menampilkan produk dalam bentuk baris ringkas padat. Cocok untuk pencarian cepat dan item banyak.
                  </p>
                </div>
              </button>
            </div>
          </div>

          {catalogViewMode === "list" && (
            <div className="pt-2">
              <SettingRow
                title="Tampilkan Gambar pada Mode Daftar (List)"
                description="Aktifkan untuk menampilkan thumbnail gambar atau ikon produk pada setiap baris daftar. Matikan jika ingin daftar lebih rapat.">
                <Toggle checked={showListImage} onChange={handleToggleListImage} />
              </SettingRow>
            </div>
          )}

          {/* Pengaturan Posisi Tab Kategori */}
          <div className="pt-4 border-t border-slate-100">
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
              Posisi Tab Kategori Produk (Tata Letak)
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => handleCategoryPositionChange("top")}
                className={`p-4 rounded-xl border-2 transition-all text-left flex items-start gap-3.5 ${
                  categoryPosition === "top"
                    ? "border-teal-500 bg-teal-50/50 ring-1 ring-teal-500/20"
                    : "border-slate-200 hover:border-teal-200 bg-white"
                }`}>
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                    categoryPosition === "top"
                      ? "bg-teal-600 text-white"
                      : "bg-slate-100 text-slate-500"
                  }`}>
                  <PanelTop className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-slate-800">Mode Atas (Horizontal)</span>
                    {categoryPosition === "top" && (
                      <span className="w-2 h-2 rounded-full bg-teal-600" />
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                    Tab kategori berjejer mendatar di atas daftar produk. Tampilan standar lapang untuk monitor PC &amp; desktop.
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleCategoryPositionChange("side")}
                className={`p-4 rounded-xl border-2 transition-all text-left flex items-start gap-3.5 ${
                  categoryPosition === "side"
                    ? "border-teal-500 bg-teal-50/50 ring-1 ring-teal-500/20"
                    : "border-slate-200 hover:border-teal-200 bg-white"
                }`}>
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                    categoryPosition === "side"
                      ? "bg-teal-600 text-white"
                      : "bg-slate-100 text-slate-500"
                  }`}>
                  <PanelLeft className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-slate-800">Mode Samping Kiri (Vertikal)</span>
                    {categoryPosition === "side" && (
                      <span className="w-2 h-2 rounded-full bg-teal-600" />
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                    Kolom kategori di samping kiri produk. Memudahkan jempol tangan kiri menggulir kategori &amp; tangan kanan memilih produk pada tablet / touchscreen.
                  </p>
                </div>
              </button>
            </div>
          </div>

          {/* Pengaturan Tampilan Stok pada Card Produk */}
          <div className="pt-4 border-t border-slate-100">
            <SettingRow
              icon={ShoppingBag}
              title="Tampilkan Informasi Stok pada Card Produk"
              description="Aktifkan untuk menampilkan sisa stok produk pada card (mode Grid maupun List) di halaman Penjualan / Kasir. Matikan jika tidak ingin kasir melihat jumlah stok.">
              <Toggle checked={showProductStock} onChange={handleToggleProductStock} />
            </SettingRow>
          </div>
        </div>
      </div>

      {/* SECTION 2: PENGATURAN BEST SELLER (MANUAL ONLY) */}
      <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm p-6">
        <SectionHeader
          icon={ShoppingBag}
          title="Tipe Penjualan Default"
          description="Pilih tipe penjualan yang otomatis digunakan setiap kali kasir membuka halaman penjualan atau memulai keranjang baru."
        />

        <div className="max-w-xl">
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Pilihan Default
          </label>
          <SearchableSelect
            options={salesTypes}
            value={defaultSalesTypeId}
            onChange={(val) => handleSaveDefaultSalesType(val)}
            placeholder={
              defaultSalesTypeLoading
                ? "Memuat tipe penjualan..."
                : salesTypes.length === 0
                  ? "Belum ada tipe penjualan aktif"
                  : "Pilih tipe penjualan"
            }
            displayKey="name"
            valueKey="id"
            searchable={true}
            disabled={defaultSalesTypeLoading || defaultSalesTypeSaving || salesTypes.length === 0}
            maxOptionsHeight="max-h-[200px]"
          />
          <p className="text-xs text-slate-500 mt-2">
            Daftar pilihan berasal dari Master Data Tipe Penjualan tenant ini.
          </p>
        </div>
      </div>

      {/* SECTION: METODE PENCATATAN HPP & INVENTORI */}
      <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm p-6">
        <SectionHeader
          icon={Calculator}
          title="Metode Pencatatan HPP & Nilai Inventori"
          description="Pilih metode penentuan Harga Pokok Penjualan (HPP) dan penilaian persediaan saat penerimaan Purchase Order (PO)."
        />

        <div className="space-y-3 max-w-3xl">
          {[
            {
              id: "STANDARD",
              title: "Standard / Master Purchase Price (Default)",
              description: "HPP dan margin laba rugi dihitung berdasarkan harga beli standar yang ditetapkan di Master Data Produk. Nilai modal stabil dan terkontrol.",
              badge: "Stabil & Terkontrol",
            },
            {
              id: "LAST_PURCHASE_PRICE",
              title: "Last Purchase Price (Harga Beli Terakhir)",
              description: "Setiap kali barang diterima dari Purchase Order, sistem otomatis memperbarui harga modal produk mengikuti harga nota aktual terbaru dari supplier.",
              badge: "Sesuai Pasar Terkini",
            },
            {
              id: "MOVING_AVERAGE",
              title: "Moving Average (Rata-rata Bergerak)",
              description: "Sistem menghitung modal rata-rata secara otomatis dari (Total Nilai Stok Lama + Nilai Belanja Baru) dibagi Total Jumlah Stok.",
              badge: "Proporsional Akuntansi",
            },
          ].map((opt) => {
            const isSelected = hppMethod === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => handleSaveHppMethod(opt.id)}
                disabled={hppMethodSaving}
                className={`w-full text-left p-4 rounded-xl border transition-all flex items-start justify-between gap-4 cursor-pointer ${
                  isSelected
                    ? "border-teal-500 bg-teal-50/40 ring-2 ring-teal-500/20"
                    : "border-slate-200 hover:border-slate-300 bg-white"
                }`}>
                <div className="flex items-start gap-3">
                  <div className="pt-0.5">
                    <input
                      type="radio"
                      name="hpp_method"
                      checked={isSelected}
                      onChange={() => handleSaveHppMethod(opt.id)}
                      className="w-4 h-4 text-teal-600 focus:ring-teal-500 border-slate-300 cursor-pointer"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-slate-800">{opt.title}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        isSelected ? "bg-teal-100 text-teal-800" : "bg-slate-100 text-slate-600"
                      }`}>
                        {opt.badge}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">{opt.description}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* SECTION 3: PENGATURAN BEST SELLER (MANUAL ONLY) */}
      <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm p-6">
        <SectionHeader
          icon={Trophy}
          title="Pengaturan Best Seller"
          description="Kelola visibilitas tab Best Seller di halaman kasir."
        />

        <div className="divide-y divide-slate-100">
          <SettingRow
            title="Aktifkan Fitur Best Seller"
            description="Tampilkan tab Best Seller di halaman penjualan kasir">
            <Toggle
              checked={bestSellerConfig.enabled}
              onChange={handleToggleBestSeller}
              disabled={bestSellerLoading || bestSellerSaving}
            />
          </SettingRow>

          {bestSellerConfig.enabled && (
            <div className="pt-4 pb-2">
              <div className="bg-teal-50/70 ring-1 ring-teal-200/80 rounded-xl p-4 flex items-start gap-3">
                <Info className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" />
                <div className="text-xs text-slate-600 leading-relaxed">
                  <p className="font-semibold text-teal-900 mb-0.5">Mode Manual Aktif:</p>
                  Penambahan produk ke daftar Best Seller kini dilakukan secara mandiri &amp; instan langsung melalui tombol <b className="text-teal-800">"Tambah Produk Best Seller"</b> di bagian bawah <b>Halaman Penjualan &gt; Tab Best Seller</b>.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* SECTION 3: KONFIGURASI SIDEBAR */}
      <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm p-6">
        <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-50 ring-1 ring-teal-200/70 flex items-center justify-center shrink-0">
              <SidebarIcon className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900 tracking-tight">
                Konfigurasi Sidebar
              </h3>
              <p className="text-xs text-slate-500">
                Atur perilaku navigasi sidebar aplikasi. Pengaturan disimpan pada perangkat ini.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              resetSidebarConfig();
              showSuccess("Pengaturan sidebar dikembalikan ke default");
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-200 rounded-lg text-xs font-semibold transition-colors shadow-sm">
            <RotateCcw className="w-3.5 h-3.5" />
            Reset Default
          </button>
        </div>

        <div className="divide-y divide-slate-100">
          <SettingRow
            icon={Hand}
            title="Maximize saat hover"
            description="Sidebar otomatis melebar ketika kursor kursor di atas sidebar (saat mode minimize).">
            <Toggle
              checked={sidebarConfig.hoverExpand}
              onChange={(v) => updateSidebarConfig({ hoverExpand: v })}
            />
          </SettingRow>

          <SettingRow
            icon={MousePointerClick}
            title="Toggle dengan klik"
            description="Tombol panah untuk minimize/maximize sidebar tetap aktif. Matikan jika ingin sidebar dikontrol murni via hover.">
            <Toggle
              checked={sidebarConfig.clickToggle}
              onChange={(v) => updateSidebarConfig({ clickToggle: v })}
            />
          </SettingRow>

          <SettingRow
            icon={Smartphone}
            title="Sembunyikan sidebar di mobile"
            description="Pada layar kecil (HP/Tablet) sidebar disembunyikan dan diakses via menu hamburger header.">
            <Toggle
              checked={sidebarConfig.mobileHide}
              onChange={(v) => updateSidebarConfig({ mobileHide: v })}
            />
          </SettingRow>
        </div>
      </div>

      {/* SECTION 4: PENGATURAN TAMPILAN APLIKASI (WEB vs APPS) */}
      <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm p-6">
        <SectionHeader
          icon={Monitor}
          title="Pengaturan Tampilan Aplikasi"
          description="Pilih ragam mode tampilan untuk kasir dan detail produk (Web Retail / Mobile Apps)."
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => handleDisplayModeChange("retail")}
            className={`p-5 rounded-xl border-2 transition-all text-left flex flex-col justify-between ${
              displayMode === "retail"
                ? "border-teal-500 bg-teal-50/50 ring-1 ring-teal-500/20 shadow-sm"
                : "border-slate-200 hover:border-teal-200 bg-white"
            }`}>
            <div>
              <div className="flex items-center justify-between mb-3">
                <div
                  className={`w-11 h-11 rounded-lg flex items-center justify-center ${
                    displayMode === "retail" ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-500"
                  }`}>
                  <Monitor className="w-5 h-5" />
                </div>
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    displayMode === "retail" ? "border-teal-600 bg-teal-600" : "border-slate-300"
                  }`}>
                  {displayMode === "retail" && <Check className="w-3 h-3 text-white stroke-[3]" />}
                </div>
              </div>
              <h4 className="font-bold text-sm text-slate-800 mb-1">Retail (Web Standard)</h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                Tampilan komprehensif standar untuk perangkat komputer / PC kasir desktop dengan mouse &amp; keyboard.
              </p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => handleDisplayModeChange("apps")}
            className={`p-5 rounded-xl border-2 transition-all text-left flex flex-col justify-between ${
              displayMode === "apps"
                ? "border-teal-500 bg-teal-50/50 ring-1 ring-teal-500/20 shadow-sm"
                : "border-slate-200 hover:border-teal-200 bg-white"
            }`}>
            <div>
              <div className="flex items-center justify-between mb-3">
                <div
                  className={`w-11 h-11 rounded-lg flex items-center justify-center ${
                    displayMode === "apps" ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-500"
                  }`}>
                  <Smartphone className="w-5 h-5" />
                </div>
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    displayMode === "apps" ? "border-teal-600 bg-teal-600" : "border-slate-300"
                  }`}>
                  {displayMode === "apps" && <Check className="w-3 h-3 text-white stroke-[3]" />}
                </div>
              </div>
              <h4 className="font-bold text-sm text-slate-800 mb-1">Kuliner / Mobile (Apps)</h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                Tampilan simpel touch-friendly yang disesuaikan untuk tablet / HP Android / resto kasir cepat.
              </p>
            </div>
          </button>
        </div>
      </div>

      {/* SECTION 5: KONFIGURASI AUTO CLOSE SHIFT */}
      <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm p-6">
        <SectionHeader
          icon={Clock}
          title="Konfigurasi Auto Close Shift"
          description="Otomatis menutup shift kasir yang masih aktif pada jam tertentu (Waktu Indonesia Barat / WIB)."
        />

        <div className="divide-y divide-slate-100">
          <SettingRow
            title="Aktifkan Auto Close Shift"
            description="Shift kasir yang masih terbuka akan ditutup secara otomatis oleh sistem pada jam yang ditentukan.">
            <Toggle
              checked={autoCloseEnabled}
              onChange={(val) => handleSaveAutoCloseShift(val, autoCloseTime)}
              disabled={autoCloseLoading || autoCloseSaving}
            />
          </SettingRow>

          {autoCloseEnabled && (
            <div className="pt-4 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Jam Auto Close Shift (WIB)</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Shift yang aktif akan otomatis ditutup saat mencapai jam ini dalam zona waktu WIB (GMT+7).
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    value={autoCloseTime}
                    onChange={(e) => setAutoCloseTime(e.target.value)}
                    onBlur={(e) => handleSaveAutoCloseShift(autoCloseEnabled, e.target.value)}
                    className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => handleSaveAutoCloseShift(autoCloseEnabled, autoCloseTime)}
                    disabled={autoCloseSaving}
                    className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold rounded-lg transition-colors shadow-xs">
                    {autoCloseSaving ? "..." : "Simpan Jam"}
                  </button>
                </div>
              </div>

              <div className="bg-teal-50/70 ring-1 ring-teal-200/80 rounded-xl p-3.5 flex items-start gap-3">
                <Info className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" />
                <div className="text-xs text-slate-600 leading-relaxed">
                  <span className="font-semibold text-teal-900">Catatan Waktu WIB:</span>
                  {" "}Shift kasir akan ditutup otomatis pada jam <b>{autoCloseTime} WIB</b>. Seluruh sisa kasir akan direkap secara otomatis dan saldo Laci Kasir akan disetorkan &amp; di-reset ke Rp 0 secara aman.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* SECTION 6: KONFIGURASI AUTO PRINT NOTA PESANAN (DRAFT PENDING) */}
      {displayMode === "apps" && (
      <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm p-6">
        <SectionHeader
          icon={Printer}
          title="Konfigurasi Auto Print Nota Pesanan (Open Bill / Draft Pending)"
          description="Otomatis mencetak nota pesanan / dapur (Open Bill / Draft Pending) saat tombol Simpan keranjang ditekan."
        />

        <div className="divide-y divide-slate-100">
          <SettingRow
            title="Aktifkan Auto Print Nota Pesanan"
            description="Setiap kali transaksi disimpan sebagai Open Bill / Draft Pending, sistem akan langsung mencetak nota pesanan secara otomatis.">
            <Toggle
              checked={autoPrintPendingEnabled}
              onChange={(val) => handleSaveAutoPrintPending(val)}
              disabled={autoCloseLoading || autoPrintPendingSaving}
            />
          </SettingRow>
        </div>
      </div>
      )}
    </div>
  );
}
